import React, { useState, useContext, useEffect } from 'react'
import { Loader } from "@googlemaps/js-api-loader"
import { createPortal } from 'react-dom';
import LoadingComponent from "./components/Loader";
import MapFilterPanel from "./components/panel/Panel";
import ViewFiltersButton from "./components/map/ViewFiltersButton";
import ViewListMapButton from "./components/map/ViewListMapButon";
import MapList from "./components/list/List";
import Map from "./components/map/Map";
import ErrorBoundary from './components/ErrorBoundary';
import { drupalSettings, loadScript } from './components/utils';
import './assets/scss/style.scss'
import { initialItems } from './components/filters/config';
import MapDetail from "./components/panel/Detail.tsx";

let map
interface mapSettings {
  host?: string,
  google_maps_api_key?: string
  lat?: number
  lon?: number
}

const mapData = JSON.parse(document.getElementById('root')?.dataset.mapData as string)

function App() {
  const [filtersActive, setFiltersActive] = useState(false)
  const [mapActive, setMapActive] = useState(true)
  const [detailsPanelActive, setDetailsPanelActive] = useState(false)
  const [activeDetail, setActiveDetail] = useState(null)
  const [activePins, setActivePins] = useState(null)
  const [activeCategories, setActiveCategories] = useState([])
  const [activeVenues, setActiveVenues] = useState([])
  const [mapSettings, setMapSettings ] = useState<mapSettings>({})
  const [richMarkerReady, setRichMarkerReady] = useState(false)
  const [cluster, setCluster] = useState(null)
  const [listTarget, setListTarget] = useState(null)
  const [showOnlyAccessible, setShowOnlyAccessible] = useState(false)
  const [ initialLoad, setInitialLoad ] = useState(true)
  const initialEls = useContext(initialItems)
  const [closureDetails, setClosureDetails] = useState(`<div>Placeholder content</div>`)
  const [closureActive, setClosureActive] = useState(false)
  const [mapLoaded, setMapLoaded] = useState(false)


  // Initialize state with undefined width/height so server and client renders match
  // Learn more here: https://joshwcomeau.com/react/the-perils-of-rehydration/
  const [windowSize, setWindowSize] = useState({
    width: undefined,
    height: undefined,
  })

  const [headerOffset, setHeaderOffset] = useState(0)

  // Handler to call on window resize
  function handleWindowResize() {
    // Set window width/height to state
    setWindowSize({
      width: window.innerWidth,
      height: window.innerHeight,
    })
  }

  const loadMap = ({ map_styling, long, lat, zoom_level }, loader) => {
    loader.importLibrary('maps').then(async (res) => {
      const { Map} = res as google.maps.MapsLibrary;
      map = new Map(document.getElementById("sopa--map") as HTMLElement, {
        center: { lat: Number(lat), lng: Number(long) },
        zoom: Number(zoom_level),
        styles: JSON.parse(map_styling)
      }) as google.maps.MapOptions
      setMapLoaded(true)
      // the richmarker script has a pre-req of Google's map overlay system being available
      // so we need to load it in after the maps are complete.
      // this version of the script was sourced from https://raw.githubusercontent.com/buddykartz/js-rich-marker/gh-pages/src/richmarker.js
      loadScript('./modules/custom/map_react/sopa--map/src/components/map/richmarker.js').then(() => {
        setRichMarkerReady(true)
      })
      // Prevent clicks on points of interest.
      map.addListener('click', event => {
        event.stop()
      })
    })
  }

  const testForAccessibility = (pins) => {
    // this tests for the presence of the accessible flag on the map pin content item
    // this should be run after any other filtering as this is an explicit choice made by the user and takes precedence of all-inclusive options.
    if (!pins) return
    pins.forEach(pin => {
      if (pin.filteredState !== false) {
        // if the pin is already turned off from other filtering, we just leave it turned off.
        // pin.filteredState = pin.map
        // console.log('pin: ', showOnlyAccessible, pin.pinIsAccessible, showOnlyAccessible && pin.pinIsAccessible, !showOnlyAccessible, pin.filteredState, pin.map)
        pin.map = (showOnlyAccessible && pin.pinIsAccessible) ? true : !showOnlyAccessible ? pin.filteredState : false
      }
    })
    // console.log(pins)
    return pins
  }

  useEffect(() => {
    if (activePins) {
      setActivePins((prevState) => {
        const filteredPins = [...prevState]
        if (!filteredPins) return prevState
        return testForAccessibility(filteredPins)
    })
  }}, [showOnlyAccessible])

  useEffect(() => {
    // loads all the settings that are required before we can start building the map.
    // the empty array at the end ensures this fires only once, acting like ComponentDidMount in class-based react.
    const settings = drupalSettings()
    if (settings && settings.mapSettings) {
      setMapSettings(prevState => {
        const newState = prevState
        for (const i in settings.mapSettings) {
          newState[i] = settings.mapSettings[i]
        }
        return { ...newState }
      })
    }
    window.addEventListener("resize", handleWindowResize)
    handleWindowResize()
  }, []);

  useEffect(() => {
    // once the map settings are loaded from the Drupal block we have enough information to load the maps.
    const loader = new Loader({
      apiKey: mapSettings.google_maps_api_key,
    })
    loadMap(mapSettings, loader)
  }, [mapSettings])

  useEffect(() => {
    setActiveVenues(() => {
      const origVenues = []
      if (!mapData) return false
      const venueList = []
      if (mapData.venue && mapData.venue != null) {
        venueList.push(mapData.venue)
      }
      if (mapData.venues && mapData.venues !== null) {
        venueList.push(...mapData.venues)
      }
      venueList.forEach(venue => {
        origVenues[venue.id] = true
      })
      // if (mapData.venues) {
      //   mapData.venues.forEach(venue => {
      //     origVenues[venue.id] = true
      //   })
      // }
      return origVenues
    })
    setActiveCategories(() => {
      const origCategories = []

      if (mapData && mapData.categories) {
        mapData.categories.forEach(category => {
          origCategories[category.id] = initialEls.categories.includes(category.id) ? true : false
        })
      }
      return origCategories
    })
    setActivePins(() => {
      const aPins = []
      mapData.pins.forEach(pin => {
        pin.map = false
        if (pin.category && initialEls.categories.includes(pin.category.id)) {
          // aPins.push(pin)
          pin.map = true
        }
      })
      return mapData.pins
      // return aPins
    })
  }, [mapData])

  const setAllFilters = (filters, isChecked) => {
    // Set all filters for a given filter type to match the state of the overall parent
    for (const i in filters) {
      filters[i] = isChecked
    }
    return filters
  }

  const setSomeFilters = (filters, isChecked, id, items) => {
    // Set parent and children filters for a nested filter selection that is not the top level
    filters[id] = isChecked
    items.forEach(item => {
        filters[item.id] = isChecked
    })
    return filters
  }

  const setFilters = (state, id, isChecked, count, items) => {
    switch (count) {
      case 'all':
        state = setAllFilters(state, isChecked, id)
        break;
      case 'some':
        state = setSomeFilters(state, isChecked, id, items)
        break;
      default:
        state[id] = isChecked
    }
    return state
  }

  const setActiveFilter = (id, isChecked, type, count,  items = []) => {
    if (type === "venues") {
      setActiveVenues(prevState => {
        const newState = setFilters(prevState, id, isChecked, count, items)
        return { ...newState }
      })
    }
    if (type === "categories") {
      setActiveCategories(prevState => {
        const newState = setFilters(prevState, id, isChecked, count, items)
          return { ...newState }
      })
    }
    setInitialLoad(false)
  }

  const handleToggleFilter = (evt: React.MouseEvent) => {
    let target = evt.target as HTMLButtonElement
    target = (target.nodeName !== 'BUTTON') ? target.closest('button') : target
    setFiltersActive(target.dataset.toggle.toString() === 'filter')
  }

  function handleToggleClick(source: string) {
    setMapActive(source === 'map')
  }

  useEffect(() => {
    // reset the height of the map to always take into account the height of the site furniture.
    // is triggered by a change in state of the window size property.
    const body = document.body
    const header = body.querySelector('header')
    const bc = body.querySelector('.nsw-drupal-messages') as HTMLElement // weird, but this is where the breadcrumbs live
    const nav = body.querySelector('#nav') as HTMLElement // main desktop menu, mobile menu does not come into the calculations.

    setHeaderOffset(header!.offsetHeight + bc?.offsetHeight + nav?.offsetHeight)
    if (windowSize.width > 991) {
      setHeaderOffset(0)
    }
  }, [windowSize])

  useEffect(() => {
    // resets the active pins if the venue or category selection is made
    if (activePins) {

      setActivePins((prevState) => {
        const filteredPins = [...prevState]
        if (!filteredPins) return prevState

        // we need to take multiple venue ids into account
        // pin.venue.id ===
        // pin.venues.foreach

        filteredPins.forEach(pin => {
          // const pvenues = []
          // if (pin.venues && pin.venues !== null) {
            //   pvenues.push(...pin.venues)
            // }
            // pvenues.push(pin.venue)
            // return item.filters[filter.field].some(itemFilterValue => checkedIds.includes(itemFilterValue))
            // console.log('pvg: ', pin.venue,  pin.venues,  activeVenues)

          pin.filteredState = activeCategories[pin.category.id] && (pin.venues && pin.venues[0].id && pin.venues.some(p => {
            // console.log(p.id, Object.keys(activeVenues), Object.keys(activeVenues).includes(p.id), activeVenues, activeVenues[p.id])
            return (Object.keys(activeVenues).includes(p.id) && activeVenues[p.id])
        })) ? true : false
          // pin.filteredState = activeCategories[pin.category.id] && (pin.venue === null || (pin.venue.id && activeVenues[pin.venue.id])) ? true : false
          pin.map = pin.filteredState

        })
        return testForAccessibility(filteredPins)
      })
      cluster && cluster.render() // google's documentation seems to be lying about this removing and re-rendering the markers.
    }
  }, [activeVenues, activeCategories])

  return (
    <div
      className="sopa-map"
      style={{
        height: `calc(100vh - ${headerOffset}px)`,
      }}
    >
      <ErrorBoundary fallback={<p>Something went wrong</p>}>
        <div className={`sopa-map--inner ${filtersActive ? 'filters-active' : 'map-active'}`}>
          <div className={`sopa-map--panel`}>
            <div className={`sopa-map--categories ${!detailsPanelActive ? 'active' : ''}`} inert={detailsPanelActive ? '' : null}>
                {richMarkerReady === true &&
                  <MapFilterPanel mapSettings={mapSettings} mapData={mapData}
                                  testForAccessibility={testForAccessibility}
                                  setFiltersActive={setFiltersActive}
                                  setActivePins={setActivePins} map={map}
                                  setActiveFilter={setActiveFilter}
                                  handleToggleFilter={handleToggleFilter}
                                  richMarkerReady={richMarkerReady}
                                  initialLoad={initialLoad}
                                  showOnlyAccessible={showOnlyAccessible}
                                  setShowOnlyAccessible={setShowOnlyAccessible}
                                  setClosureActive={setClosureActive}
                                  closureActive={closureActive}
                                  setClosureDetails={setClosureDetails}
                                  activePins={activePins}/>}
            </div>
            <div aria-live="polite" className={`sopa-map--details ${detailsPanelActive ? 'active' : ''}`} inert={!detailsPanelActive ? '' : null}>
                <MapDetail activeDetail={activeDetail}
                                setDetailsPanelActive={setDetailsPanelActive}
                                mapData={mapData}
                                handleToggleFilter={handleToggleFilter}/>
            </div>
          </div>
          <div className={`sopa-map--content`}>
              <div className="sopa-map--view">
                <div className="sopa-map--view-toggle sopa--mobile-only">
                  <ViewFiltersButton handleToggleFilter={handleToggleFilter} />
                </div>
                <div className="sopa-map--view-toggle ">
                  <ViewListMapButton handleToggleClick={handleToggleClick} mapActive={mapActive} />
                </div>
              </div>
            <div className={mapActive ? 'active sopa-map--map' : 'sopa-map--map'} inert={!mapActive ? '' : null}>
                {richMarkerReady === true &&
                  <Map map={map} activePins={activePins}
                    cluster={cluster} setCluster={setCluster}
                    handleToggleFilter={handleToggleFilter}
                    activeDetail={activeDetail} setMapActive={setMapActive}
                    setActiveDetail={setActiveDetail} mapData={mapData}
                    listTarget={listTarget}
                    setDetailsPanelActive={setDetailsPanelActive}
                    setClosureActive={setClosureActive}
                    closureActive={closureActive}
                    mapLoaded={mapLoaded}
                    setClosureDetails={setClosureDetails}
                  />}
              <div id="sopa--map"></div>
            </div>
            <div className={!mapActive ? 'active sopa-map--list' : 'sopa-map--list'} inert={mapActive ? '' : null}>
                <MapList activeDetail={activeDetail}
                  activePins={activePins}
                  setListTarget={setListTarget}
                  setMapActive={setMapActive}
                  setActiveDetail={setActiveDetail} mapData={mapData}
                  setDetailsPanelActive={setDetailsPanelActive}/>
            </div>
            <div className={`sopa-map--closure ${closureActive ? 'active' : 'not-active'}`}>
              <button draggable="false" aria-label="Close" className='material-icons' title="Close" onClick={() => { setClosureActive(false)}} type="button">close</button>
              <div dangerouslySetInnerHTML={{ __html: closureDetails }}></div>
            </div>
          </div>
        </div>
      </ErrorBoundary>
        {createPortal(
        <>
            {richMarkerReady === false && <LoadingComponent/>}
        </>,
        document.getElementById('root')
      )}
    </div>
  )
}

export default App
